import { useEffect, useEffectEvent, useRef } from 'react';

import { socket } from '@mint-sdk/api-media-plan/socket';

import { useActivitySummaryApi } from '../../api/queries/useActivitySummaryApi.js';
import { useColumnsApi } from '../../api/queries/useColumnsApi.js';
import { useFlightSummaryApi } from '../../api/queries/useFlightSummaryApi.js';
import { useMediaPlanSummaryApi } from '../../api/queries/useMediaPlanSummaryApi.js';
import { usePreviewActivityListApi } from '../../api/queries/usePreviewActivityListApi.js';
import { useSidebarTreeApi } from '../../api/queries/useSidebarTreeApi.js';
import { usePlanningGridApi } from '../../atoms/gridApi.js';
import { MP_LEVELS } from '../../consts/index.js';
import { useChosenLevel } from '../useChosenLevel.js';

export const useRefreshOnReconnect = () => {
  const [gridApi] = usePlanningGridApi();
  const { level } = useChosenLevel();
  const { refetch: refetchPreviewActivityList } = usePreviewActivityListApi();
  const { refetch: refetchColumns } = useColumnsApi({ enabled: false });
  const { refetch: refetchSidebarTree } = useSidebarTreeApi();

  const { refetch: refetchMediaPlanSummary } = useMediaPlanSummaryApi({
    enabled: false,
  });
  const { refetch: refetchActivitySummary } = useActivitySummaryApi({
    enabled: false,
  });
  const { refetch: refetchFlightSummary } = useFlightSummaryApi({
    enabled: false,
  });

  const refreshGridAndStaleResources = () => {
    refetchPreviewActivityList();
    refetchColumns();

    if (gridApi) {
      const groupLevelState = gridApi.getServerSideGroupLevelState();

      groupLevelState.forEach((levelState) => {
        gridApi.refreshServerSide({ route: levelState.route });
      });
    }
  };

  const refreshSummaryAndSidebar = () => {
    const refetchSummaryMap = {
      [MP_LEVELS.MEDIA_PLAN]: refetchMediaPlanSummary,
      [MP_LEVELS.ACTIVITY]: refetchActivitySummary,
      [MP_LEVELS.FLIGHT]: refetchFlightSummary,
    };

    const refetchSummary = refetchSummaryMap[level];

    refetchSummary();
    refetchSidebarTree();
  };

  const wasDisconnectedRef = useRef(false);

  const handleDisconnect = useEffectEvent(() => {
    wasDisconnectedRef.current = true;
  });

  const handleConnect = useEffectEvent(() => {
    if (wasDisconnectedRef.current) {
      refreshSummaryAndSidebar();
      refreshGridAndStaleResources();
      wasDisconnectedRef.current = false;
    }
  });

  const handleOnline = useEffectEvent(() => {
    if (wasDisconnectedRef.current) {
      // wait for socket reconnect
      return;
    }

    refreshGridAndStaleResources();
  });

  useEffect(() => {
    const client = socket.getClient();

    client.on('disconnect', handleDisconnect);
    client.on('connect', handleConnect);

    globalThis.addEventListener('online', handleOnline);

    return () => {
      globalThis.removeEventListener('online', handleOnline);
      client.off('connect');
      client.off('disconnect');
    };
  }, []);
};
